import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const STATE_COLORS = {
  MACHINE_EXTRACTED: 'bg-zinc-800 text-zinc-300 border border-zinc-700',
  UNREVIEWED: 'bg-zinc-800 text-zinc-400 border border-zinc-700',
  ACCEPT_FOR_EVIDENCE_REVIEW: 'bg-emerald-950 text-emerald-400 border border-emerald-800',
  HELD: 'bg-amber-950 text-amber-400 border border-amber-800',
  ESCALATED: 'bg-orange-950 text-orange-400 border border-orange-800',
  REJECTED: 'bg-red-950 text-red-400 border border-red-900',
};

const SYNTHETIC_BADGE = (
  <span className="ml-2 text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-400 border border-zinc-600 font-mono">
    SYNTHETIC FIXTURE
  </span>
);

function Badge({ label, colorClass }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded font-mono tracking-wide ${colorClass}`}>
      {label}
    </span>
  );
}

function ReviewPanel({ item, type, onReviewed }) {
  const [action, setAction] = useState('');
  const [note, setNote] = useState('');
  const [reviewer, setReviewer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const NOTE_REQUIRED = ['reject', 'hold', 'escalate'];
  const ACTIONS = [
    { value: 'accept_for_evidence_review', label: 'Accept for Evidence Review' },
    { value: 'reject', label: 'Reject' },
    { value: 'hold', label: 'Hold' },
    { value: 'escalate', label: 'Escalate' },
  ];

  async function handleSubmit(e) {
    e.preventDefault();
    if (!action || !reviewer) { setError('Action and reviewer required.'); return; }
    if (NOTE_REQUIRED.includes(action) && !note.trim()) {
      setError(`A note is required for "${action}".`); return;
    }
    setSubmitting(true); setError('');
    try {
      const endpoint = type === 'claim'
        ? `/evidence/graph/claims/${item.id}/review`
        : `/evidence/graph/constraints/${item.id}/review`;
      await api.post(endpoint, { action, note, reviewer });
      setAction(''); setNote(''); setReviewer('');
      onReviewed();
    } catch (err) {
      setError(err.response?.data?.detail || 'Review failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 space-y-2 border-t border-zinc-800 pt-3">
      <div className="flex gap-2">
        <select
          data-testid={`review-action-${item.id}`}
          value={action}
          onChange={e => setAction(e.target.value)}
          className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-200 focus:outline-none focus:border-emerald-500"
        >
          <option value="">Select action…</option>
          {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <input
          data-testid={`review-reviewer-${item.id}`}
          placeholder="Reviewer name"
          value={reviewer}
          onChange={e => setReviewer(e.target.value)}
          className="flex-1 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-emerald-500"
        />
      </div>
      {NOTE_REQUIRED.includes(action) && (
        <textarea
          data-testid={`review-note-${item.id}`}
          placeholder="Note required for this action…"
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={2}
          className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-300 placeholder-zinc-600 resize-none focus:outline-none focus:border-emerald-500"
        />
      )}
      {error && <p className="text-xs text-red-400">{error}</p>}
      <p className="text-xs text-zinc-600">
        ACCEPT FOR EVIDENCE REVIEW means this candidate may proceed into evidence review — not accepted evidence, not institutional canon.
      </p>
      <button
        type="submit"
        data-testid={`review-submit-${item.id}`}
        disabled={submitting}
        className="px-3 py-1 text-sm rounded bg-emerald-700 hover:bg-emerald-600 text-black font-medium disabled:opacity-50"
      >
        {submitting ? 'Submitting…' : 'Submit Review'}
      </button>
    </form>
  );
}

function CandidateCard({ item, type, onReviewed }) {
  const [expanded, setExpanded] = useState(false);
  const rs = STATE_COLORS[item.review_state] || STATE_COLORS.UNREVIEWED;
  const es = STATE_COLORS[item.extraction_state] || STATE_COLORS.MACHINE_EXTRACTED;

  return (
    <div
      data-testid={`candidate-card-${item.id}`}
      className="border border-zinc-800 rounded-lg p-4 space-y-2 hover:border-zinc-700 transition-colors"
    >
      <div className="flex flex-wrap items-start gap-2">
        <Badge label={item.extraction_state} colorClass={es} />
        <Badge label={item.review_state} colorClass={rs} />
        {item.is_synthetic_fixture && SYNTHETIC_BADGE}
        <span className="ml-auto text-xs text-zinc-600 font-mono">{item.anchor}</span>
      </div>
      <p data-testid={`candidate-text-${item.id}`} className="text-sm text-zinc-300 leading-relaxed">
        {item.text}
      </p>
      <div className="flex gap-4 text-xs text-zinc-600">
        <span>Source: <span className="text-zinc-400">{item.source_title}</span></span>
        {item.constraint_keywords?.length > 0 && (
          <span>Keywords: <span className="text-amber-500">{item.constraint_keywords.join(', ')}</span></span>
        )}
      </div>
      <div className="flex gap-2 text-xs text-zinc-600">
        <span>Provenance: <span className="text-zinc-500">{item.source_version_id}</span></span>
      </div>
      {item.review_state !== 'REJECTED' && (
        <button
          data-testid={`review-toggle-${item.id}`}
          onClick={() => setExpanded(v => !v)}
          className="text-xs text-emerald-500 hover:text-emerald-400"
        >
          {expanded ? 'Hide review' : 'Review this candidate'}
        </button>
      )}
      {expanded && <ReviewPanel item={item} type={type} onReviewed={onReviewed} />}
    </div>
  );
}

export default function Evidence() {
  const [tab, setTab] = useState('evidence');
  const [evidence, setEvidence] = useState([]);
  const [claims, setClaims] = useState([]);
  const [constraints, setConstraints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [evRes, clRes, cnRes] = await Promise.all([
        api.get('/evidence'),
        api.get('/evidence/graph/claims'),
        api.get('/evidence/graph/constraints'),
      ]);
      setEvidence(evRes.data.evidence || []);
      setClaims(clRes.data.claims || []);
      setConstraints(cnRes.data.constraints || []);
    } catch {
      setError('Failed to load evidence graph data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { id: 'evidence', label: 'Evidence Records', count: evidence.length },
    { id: 'claims', label: 'Claims', count: claims.length },
    { id: 'constraints', label: 'Constraints', count: constraints.length },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="evidence-page">
      <div>
        <h1 className="text-2xl font-bold text-zinc-100 font-['IBM_Plex_Sans']">Evidence Graph</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Machine-extracted candidate claims and constraints from governed sources.
          All candidates start as <span className="font-mono text-zinc-400">MACHINE_EXTRACTED / UNREVIEWED</span>.
          No candidate is automatically promoted.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'text-emerald-400 border-b-2 border-emerald-500'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {t.label}
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-500">
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-zinc-500 text-sm">Loading evidence graph…</div>
      )}
      {error && (
        <div className="text-red-400 text-sm border border-red-900 rounded p-3">{error}</div>
      )}

      {!loading && !error && (
        <>
          {/* Evidence Records Tab */}
          {tab === 'evidence' && (
            <div className="space-y-3">
              {evidence.length === 0 ? (
                <p className="text-zinc-600 text-sm">No evidence records found.</p>
              ) : evidence.map(ev => (
                <div key={ev.id} data-testid={`evidence-record-${ev.id}`}
                  className="border border-zinc-800 rounded-lg p-4 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-zinc-200">{ev.type}</span>
                    <span className="text-xs text-zinc-600 font-mono">{ev.id?.slice(0, 8)}</span>
                  </div>
                  {ev.description && (
                    <p className="text-sm text-zinc-400">{ev.description}</p>
                  )}
                  <div className="text-xs text-zinc-600">
                    Source: <span className="text-zinc-500">{ev.source}</span>
                    {ev.label && <span className="ml-3">Label: <span className="text-zinc-500">{ev.label}</span></span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Claims Tab */}
          {tab === 'claims' && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-600 border border-zinc-800 rounded p-3">
                <strong className="text-zinc-400">Evidence hierarchy:</strong> Canon-locked source passages are the primary governed substrate.
                Claims labeled CANDIDATE_ANALYTICAL are accepted for evidence review but are NOT established canon.
                Unreviewed candidates are for review purposes only.
              </div>
              {claims.length === 0 ? (
                <p className="text-zinc-600 text-sm">No claim candidates found.</p>
              ) : claims.map(c => (
                <CandidateCard key={c.id} item={c} type="claim" onReviewed={load} />
              ))}
            </div>
          )}

          {/* Constraints Tab */}
          {tab === 'constraints' && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-600 border border-zinc-800 rounded p-3">
                <strong className="text-zinc-400">Constraint candidates</strong> are machine-extracted passages containing
                obligation keywords (must, shall, required, prohibited, etc.).
                Review state does NOT imply institutional canon acceptance.
              </div>
              {constraints.length === 0 ? (
                <p className="text-zinc-600 text-sm">No constraint candidates found.</p>
              ) : constraints.map(c => (
                <CandidateCard key={c.id} item={c} type="constraint" onReviewed={load} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
